import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, MaskLineInView } from "../Reveal";
import { DEMOS, SORA_IMAGES, VERDANT_IMAGES, SPARKLE_IMAGES } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { ArrowUpRight } from "lucide-react";

// One extra frame per demo, reusing an image that already ships with that
// demo's own detail page — no new asset, just a second crop of it here.
const OVERLAY_IMAGE = {
  "sora-sushi": SORA_IMAGES.b,
  "verdant-landscapes": VERDANT_IMAGES.b,
  "sparkle-shine": SPARKLE_IMAGES.b,
};

export const Portfolio = () => {
  const { t } = useLanguage();
  return (
    <section id="portfolio" data-testid="portfolio-demos" className="py-24 md:py-36 bg-charcoal text-cream">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <h2 className="font-display text-5xl md:text-7xl tracking-tight text-cream">
            <MaskLineInView>{t.portfolio.heading[0]}</MaskLineInView>
            <MaskLineInView delay={0.1} className="text-gold">{t.portfolio.heading[1]}</MaskLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-cream/60 md:text-right">{t.portfolio.intro}</p>
          </Reveal>
        </div>

        <div className="space-y-6">
          {DEMOS.map((d, i) => {
            const dt = t.demos[d.slug] || {};
            return (
              <Reveal key={d.slug} delay={i * 0.08}>
                <Link
                  to={`/demos/${d.slug}`}
                  data-testid={`demo-card-${d.slug}`}
                  className="group grid md:grid-cols-12 gap-6 md:gap-10 items-center border-t border-cream/20 pt-6"
                >
                  <div className={`md:col-span-5 order-2 ${i % 2 ? "md:order-2" : "md:order-1"}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="micro text-gold">0{i + 1}</span>
                      <span className="micro text-cream/50">{dt.tag || d.tag}</span>
                    </div>
                    <h3 className="font-display text-4xl md:text-6xl text-cream group-hover:text-gold transition-colors duration-500">
                      {d.name}
                    </h3>
                    <span className="mt-6 inline-flex items-center gap-2 micro text-gold btn-underline">
                      {t.portfolio.view}
                      <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </span>
                  </div>
                  {/* Crop marks sit on the outer wrapper: the inner element clips
                      its overflow for the image zoom, which would swallow them.
                      A second, smaller frame overlaps the main shot — a layered
                      grid moment rather than a single flat photo. */}
                  <div className={`md:col-span-7 order-1 ${i % 2 ? "md:order-1" : "md:order-2"} crop-frame crop-frame-light relative`}>
                    <div className="overflow-hidden rounded-2xl">
                      <motion.img
                        src={d.cover}
                        alt={d.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-[280px] md:h-[420px] object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                      />
                    </div>
                    <div className={`absolute -bottom-6 ${i % 2 ? "-right-6" : "-left-6"} hidden md:block w-[38%] aspect-[4/5] rounded-xl overflow-hidden border-4 border-charcoal shadow-[0_20px_45px_-15px_rgba(0,0,0,0.55)] transition-transform duration-500 ease-out group-hover:-translate-y-1 group-hover:-translate-x-1`}>
                      <img
                        src={OVERLAY_IMAGE[d.slug]}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
